import React, { useState, useEffect } from 'react';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
/**
 * WeeklyDigestPreferences - Manage weekly email digest settings
 * Features witty, satirical humor from "Letters to Evelyn" by John Selman
 */
const WeeklyDigestPreferences = ({ token, showToast }) => {
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [preferences, setPreferences] = useState({
    weekly_digest_enabled: true,
    digest_day: 'monday',
    include_friend_activity: true,
    include_achievements: true,
    include_stats: true,
    include_humor: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [sampleJokes, setSampleJokes] = useState([]);

  // Days of week for digest scheduling
  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Fetch current preferences
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const [prefsData, jokesData] = await Promise.all([
          get('/email-digest/digest/preferences'),
          get('/email-digest/digest/jokes')
        ]);
        
        setPreferences({
          weekly_digest_enabled: prefsData.weekly_digest_enabled !== false,
          digest_day: prefsData.digest_day || 'monday',
          include_friend_activity: prefsData.include_friend_activity !== false,
          include_achievements: prefsData.include_achievements !== false,
          include_stats: prefsData.include_stats !== false,
          include_humor: prefsData.include_humor !== false
        });
        
        setSampleJokes(jokesData.sample_jokes || []);
      } catch (e) {
        console.error('Failed to fetch digest preferences:', e);
      }
      setLoading(false);
    };
    
    fetchData();
  }, [get, token]);

  const savePreferences = async () => {
    setSaving(true);
    try {
      await put('/email-digest/digest/preferences', { body: preferences });
      showToast && showToast('Digest preferences saved!', 'success');
    } catch (e) {
      showToast && showToast('Error saving preferences', 'error');
    }
    setSaving(false);
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const data = await post('/email-digest/digest/send-test');
      showToast && showToast(
        data.mock_mode 
          ? 'Test digest logged (check server logs)' 
          : 'Test digest sent to your email!', 
        'success'
      );
    } catch (e) {
      showToast && showToast('Error sending test digest', 'error');
    }
    setSendingTest(false);
  };

  const loadPreview = async () => {
    setShowPreview(!showPreview);
    if (showPreview || previewData) return;
    
    try {
      get('/email-digest/digest/preview', {
      onSuccess: (data) => setPreviewData(data)
    })
    } catch (e) {
      console.error('Failed to load preview:', e);
    }
  };

  const unsubscribe = async () => {
    if (!window.confirm('Are you sure you want to unsubscribe from weekly digests?')) return;
    
    try {
      const data = await post('/email-digest/digest/unsubscribe');
      setPreferences(prev => ({ ...prev, weekly_digest_enabled: false }));
      showToast && showToast(data.message || 'Unsubscribed from weekly digests', 'success');
    } catch (e) {
      showToast && showToast('Error unsubscribing', 'error');
    }
  };

  const contentOptions = [
    { key: 'include_stats', label: 'Activity Statistics', icon: '📊', description: 'Your searches, categorizations, and URLs added' },
    { key: 'include_friend_activity', label: 'Friend Activity', icon: '👥', description: 'What your friends shared with you' },
    { key: 'include_achievements', label: 'Achievements', icon: '🏆', description: 'Milestones and rewards earned' },
    { key: 'include_humor', label: 'Witty Jokes', icon: '😂', description: 'Satirical humor from "Letters to Evelyn"' }
  ];

  if (loading) {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        color: '#a1a1aa'
      }}>
        Loading digest preferences...
      </div>
    );
  }

  return (
    <div data-testid="weekly-digest-preferences" style={{
      background: 'linear-gradient(135deg, rgba(253, 230, 138, 0.1), rgba(253, 224, 71, 0.1))',
      border: '1px solid rgba(253, 230, 138, 0.2)',
      borderRadius: 16,
      padding: 24,
      marginTop: 20
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: 8
      }}>
        <h3 style={{ 
          margin: 0, 
          color: '#0F172A',
          fontSize: '1.1rem',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <span style={{ fontSize: '1.3rem' }}>📬</span> Weekly Email Digest
        </h3>
        
        {/* Master toggle */}
        <button
          onClick={() => togglePreference('weekly_digest_enabled')}
          style={{
            width: 60,
            height: 32,
            borderRadius: 16,
            background: preferences.weekly_digest_enabled 
              ? 'linear-gradient(135deg, #FDE68A, #FDE047)' 
              : 'rgba(255,255,255,0.1)',
            border: 'none',
            cursor: 'pointer',
            position: 'relative',
            transition: 'all 0.3s'
          }}
          data-testid="toggle-weekly-digest"
        >
          <div style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            background: '#fff',
            position: 'absolute',
            top: 3,
            left: preferences.weekly_digest_enabled ? 31 : 3,
            transition: 'left 0.3s',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
          }} />
        </button>
      </div>
      
      <p style={{ 
        color: '#a1a1aa', 
        fontSize: '0.85rem', 
        marginBottom: 20,
        lineHeight: 1.5
      }}>
        Get a weekly summary of your InfoPilot activity with witty, satirical humor 
        from "Letters to Evelyn" by John Selman
      </p>

      {preferences.weekly_digest_enabled && (
        <>
          {/* Sample Joke Preview */}
          {sampleJokes.length > 0 && preferences.include_humor && (
            <div style={{
              background: 'rgba(253, 224, 71, 0.15)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              borderLeft: '4px solid #FDE047'
            }}>
              <p style={{ 
                color: '#c4b5fd', 
                fontSize: '0.9rem', 
                fontStyle: 'italic',
                margin: 0,
                lineHeight: 1.6
              }}>
                "{sampleJokes[Math.floor(Math.random() * sampleJokes.length)]}"
              </p>
              <p style={{ 
                color: '#6b7280', 
                fontSize: '0.75rem', 
                margin: '10px 0 0 0'
              }}>
                — Sample joke from your next digest
              </p>
            </div>
          )}

          {/* Schedule Day */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ 
              display: 'block', 
              color: '#047857', 
              fontSize: '0.85rem', 
              marginBottom: 8,
              fontWeight: 600
            }}>
              📅 Delivery Day
            </label>
            <div style={{ 
              display: 'flex', 
              gap: 8, 
              flexWrap: 'wrap' 
            }}>
              {daysOfWeek.map(day => (
                <button
                  key={day.value}
                  onClick={() => setPreferences(prev => ({ ...prev, digest_day: day.value }))}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 20,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: preferences.digest_day === day.value ? 600 : 400,
                    background: preferences.digest_day === day.value 
                      ? 'linear-gradient(135deg, #FDE68A, #FDE047)' 
                      : 'rgba(203, 213, 225, 0.8)',
                    color: preferences.digest_day === day.value ? '#fff' : '#a1a1aa',
                    transition: 'all 0.2s'
                  }}
                  data-testid={`digest-day-${day.value}`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Options */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ 
              display: 'block', 
              color: '#047857', 
              fontSize: '0.85rem', 
              marginBottom: 12,
              fontWeight: 600
            }}>
              📝 Content Included
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {contentOptions.map(item => (
                <div 
                  key={item.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    background: preferences[item.key] 
                      ? 'rgba(253, 230, 138, 0.1)' 
                      : 'rgba(255, 255, 255, 0.85)',
                    borderRadius: 10,
                    border: `1px solid ${preferences[item.key] ? 'rgba(253, 230, 138, 0.25)' : 'transparent'}`,
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
                    <div>
                      <div style={{ color: '#e5e5e5', fontWeight: 500, fontSize: '0.85rem' }}>
                        {item.label}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>
                        {item.description}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => togglePreference(item.key)}
                    style={{
                      width: 44,
                      height: 24,
                      borderRadius: 12,
                      background: preferences[item.key] 
                        ? 'linear-gradient(135deg, #FDE68A, #FDE047)' 
                        : 'rgba(255,255,255,0.1)',
                      border: 'none',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s'
                    }}
                    data-testid={`toggle-${item.key}`}
                  >
                    <div style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 3,
                      left: preferences[item.key] ? 23 : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Preview Section */}
          <div style={{ marginBottom: 20 }}>
            <button
              onClick={loadPreview}
              style={{
                width: '100%',
                padding: '10px',
                background: 'rgba(245, 158, 11, 0.15)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: 10,
                color: '#3b82f6',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
              data-testid="preview-digest-btn"
            >
              <span>👁️</span>
              {showPreview ? 'Hide Preview' : 'Preview Your Next Digest'}
            </button>
            
            {showPreview && previewData && (
              <div style={{
                marginTop: 15,
                padding: 15,
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: 12
              }}>
                <div style={{ 
                  color: '#047857', 
                  fontSize: '0.8rem', 
                  marginBottom: 10 
                }}>
                  Subject: <strong>{previewData.subject}</strong>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10,
                  marginBottom: 10
                }}>
                  <div style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    padding: 10,
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#3b82f6', fontSize: '1.2rem', fontWeight: 700 }}>
                      {previewData.stats_preview?.searches || 0}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>Searches</div>
                  </div>
                  <div style={{
                    background: 'rgba(253, 224, 71, 0.1)',
                    padding: 10,
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#FDE047', fontSize: '1.2rem', fontWeight: 700 }}>
                      {previewData.stats_preview?.categorizations || 0}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>Categorized</div>
                  </div>
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    padding: 10,
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#10b981', fontSize: '1.2rem', fontWeight: 700 }}>
                      {previewData.stats_preview?.new_categories || 0}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>New Categories</div>
                  </div>
                  <div style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    padding: 10,
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <div style={{ color: '#f59e0b', fontSize: '1.2rem', fontWeight: 700 }}>
                      {previewData.stats_preview?.urls_added || 0}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>URLs Added</div>
                  </div>
                </div>
                <div style={{
                  background: 'rgba(253, 224, 71, 0.1)',
                  padding: 12,
                  borderRadius: 8,
                  fontStyle: 'italic',
                  color: '#c4b5fd',
                  fontSize: '0.85rem',
                  lineHeight: 1.5
                }}>
                  "{previewData.joke_preview}"
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
            <button
              onClick={sendTestDigest}
              disabled={sendingTest}
              style={{
                flex: 1,
                padding: '12px',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 10,
                color: '#10b981',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                opacity: sendingTest ? 0.7 : 1
              }}
              data-testid="send-test-digest-btn"
            >
              {sendingTest ? '📧 Sending...' : '📧 Send Test Email'}
            </button>
          </div>
        </>
      )}

      {/* Save button */}
      <button
        onClick={savePreferences}
        disabled={saving}
        style={{
          width: '100%',
          padding: '12px',
          background: 'linear-gradient(135deg, #FDE68A, #FDE047)',
          border: 'none',
          borderRadius: 10,
          color: '#0F172A',
          fontWeight: 600,
          fontSize: '0.95rem',
          cursor: 'pointer',
          opacity: saving ? 0.7 : 1,
          transition: 'opacity 0.2s'
        }}
        data-testid="save-digest-preferences"
      >
        {saving ? 'Saving...' : 'Save Digest Preferences'}
      </button>

      {/* Unsubscribe option */}
      {preferences.weekly_digest_enabled && (
        <button
          onClick={unsubscribe}
          style={{
            width: '100%',
            padding: '10px',
            marginTop: 10,
            background: 'transparent',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 10,
            color: '#ef4444',
            fontWeight: 500,
            fontSize: '0.8rem',
            cursor: 'pointer'
          }}
          data-testid="unsubscribe-digest-btn"
        >
          Unsubscribe from Weekly Digests
        </button>
      )}
    </div>
  );
};

export default WeeklyDigestPreferences;
